import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaBars, FaTimes, FaChevronDown, FaHome, FaUser, FaBuilding, FaRoute, FaWallet, FaSignOutAlt } from "react-icons/fa";
import { clearCorporateSession, getCorporateMember, isValidCorporateSession } from "../utils/corporateSession";

const CorporateLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Validate corporate session on mount
    if (!isValidCorporateSession()) {
      console.warn("⚠️ CorporateLayout: Invalid or missing corporate session");
      navigate("/corporate/login", { replace: true });
      return;
    }

    // Initialize member data on component mount
    const member = getCorporateMember();
    setMember(member);

    const onStorage = (e) => {
      if (e.key === "corporateMember" || e.key === "corporateToken") {
        const updatedMember = getCorporateMember();
        setMember(updatedMember);
      }
    };

    const onSessionChanged = () => {
      const updatedMember = getCorporateMember();
      setMember(updatedMember);
    };

    const onSessionRestored = () => {
      const member = getCorporateMember();
      if (member) {
        setMember(member);
      }
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("corporateSessionChanged", onSessionChanged);
    window.addEventListener("corporateSessionRestored", onSessionRestored);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("corporateSessionChanged", onSessionChanged);
      window.removeEventListener("corporateSessionRestored", onSessionRestored);
    };
  }, [navigate]);

  const logout = () => {
    clearCorporateSession();
    sessionStorage.removeItem("corporateBookingMode");
    window.dispatchEvent(new Event("corporateSessionChanged"));
    navigate("/corporate/login", { replace: true });
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const navItems = [
    { path: "/corporate/admin-dashboard", name: "Dashboard" },
    { 
      name: "Company & Employees", 
      icon: <FaBuilding className="mr-2 text-[#F56753]/80" />,
      dropdown: [
        { path: "/corporate/staff", name: "Staff Management" },
        { path: "/corporate/profile", name: "Wallet Summary" },
      ]
    },
    { path: "/corporate/bookings", name: "Bookings" },
    { path: "/corporate/wallet", name: "Cabzigo Wallet" },
    { 
      name: "Travel Management", 
      icon: <FaRoute className="mr-2 text-amber-500/80" />,
      dropdown: [
        { path: "/corporate/pending-approvals", name: "Pending Approvals" },
        { path: "/corporate/approved", name: "Approved Bookings" },
        { path: "/corporate/rejected", name: "Rejected Bookings" },
      ]
    },
  ];

  const getInitials = (name) => {
    if (!name) return "C";
    return name.charAt(0).toUpperCase();
  };

  const toggleDropdown = (name) => {
    setDropdownOpen(dropdownOpen === name ? null : name);
    setProfileDropdownOpen(false);
  };

  const toggleProfileDropdown = () => {
    setProfileDropdownOpen(!profileDropdownOpen);
    setDropdownOpen(null);
  };

  return (
    <div className="min-h-screen bg-slate-50/50 flex flex-col font-sans">
      {/* Click-outside Backdrops */}
      {dropdownOpen && (
        <div className="fixed inset-0 z-40 bg-transparent" onClick={() => setDropdownOpen(null)} />
      )}
      {profileDropdownOpen && (
        <div className="fixed inset-0 z-40 bg-transparent" onClick={() => setProfileDropdownOpen(false)} />
      )}
      {/* Mobile menu backdrop - closes menu when clicking outside */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/30 backdrop-blur-[1px]"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Top Navigation Header - Fixed */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 gap-x-6">
            {/* Logo */}
            <div className="flex items-center mr-8 lg:mr-12">
              <Link to="/" className="flex items-center group">
                <span className="text-xl font-extrabold tracking-tight flex items-center">
                  <span className="bg-gradient-to-r from-[#F56753] to-[#d94e3a] bg-clip-text text-transparent group-hover:opacity-90 transition-opacity">Cab</span>
                  <span className="text-slate-800">Zigo</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F56753] ml-0.5 animate-pulse"></span>
                  <span className="ml-1.5 text-xs font-bold text-slate-400 border border-slate-200 px-1.5 py-0.5 rounded-md uppercase tracking-wider bg-slate-50">Admin</span>
                </span>
              </Link>
            </div>

            {/* Navigation Menu (Desktop) */}
            <nav className="hidden lg:flex items-center space-x-2.5 xl:space-x-4 z-50">
              {navItems.map((item) => (
                item.dropdown ? (
                  <div key={item.name} className="relative">
                    <button
                      onClick={() => toggleDropdown(item.name)}
                      className={`flex items-center gap-1 px-3.5 py-2 text-sm font-semibold rounded-xl transition-all duration-200 ${
                        item.dropdown.some(sub => isActive(sub.path))
                          ? "bg-orange-50 text-[#F56753]"
                          : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                      }`}
                    >
                      {item.name}
                      <FaChevronDown className={`ml-1 text-[10px] opacity-75 transition-transform duration-300 ${dropdownOpen === item.name ? "rotate-180" : ""}`} />
                    </button>
                    {dropdownOpen === item.name && (
                      <div className="absolute top-full left-0 mt-2 w-56 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-slate-100 p-1.5 z-50 animate-in fade-in-50 slide-in-from-top-2 duration-150">
                        {item.dropdown.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            onClick={() => setDropdownOpen(null)}
                            className={`flex items-center px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                              isActive(subItem.path)
                                ? "bg-[#F56753] text-white shadow-sm shadow-orange-100"
                                : "text-slate-700 hover:bg-slate-50"
                            }`}
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`px-3.5 py-2 text-sm font-semibold rounded-xl transition-all duration-200 ${
                      isActive(item.path)
                        ? "bg-slate-900 text-white"
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </nav>

            {/* Right Side Controls */}
            <div className="flex items-center space-x-3.5 z-50">
              {/* Home Redirection */}
              <Link
                to="/corporate/dashboard"
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-50 to-orange-50/40 border border-orange-100 text-[#F56753] rounded-xl text-sm font-bold shadow-sm shadow-orange-50/50 hover:shadow hover:bg-orange-100/80 transition-all duration-200"
              >
                <FaHome className="text-[#F56753]" />
                Cabzigo Home
              </Link>

              {/* Profile Dropdown */}
              <div className="relative">
                <button
                  onClick={toggleProfileDropdown}
                  className="flex items-center space-x-2.5 focus:outline-none p-1 hover:bg-slate-50 rounded-xl transition-all"
                >
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-[#F56753] to-[#d94e3a] flex items-center justify-center text-white font-extrabold shadow-sm border border-white overflow-hidden text-sm uppercase">
                    {member?.profileImageUrl ? (
                      <img src={member.profileImageUrl} alt={member?.fullName || "User"} className="w-full h-full object-cover" />
                    ) : (
                      getInitials(member?.fullName || member?.email)
                    )}
                  </div>

                  <div className="hidden lg:block text-left max-w-[120px]">
                    <p className="text-xs font-bold text-slate-800 truncate leading-snug">
                      {member?.fullName || 'Corporate Admin'}
                    </p>
                    <p className="text-[10px] text-slate-400 truncate mt-0.5 leading-none">
                      {member?.email || ''}
                    </p>
                  </div>

                  <FaChevronDown 
                    className={`text-slate-400 text-[10px] transition-transform duration-300 ${
                      profileDropdownOpen ? "rotate-180" : ""
                    }`} 
                  />
                </button>

                {profileDropdownOpen && (
                  <div className="absolute top-full right-0 mt-2 w-64 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-slate-100 p-2 z-50 animate-in fade-in-50 slide-in-from-top-2 duration-150">
                    {/* User profile card block */}
                    <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 rounded-t-xl mb-1 text-left">
                      <div className="flex items-center gap-2.5 mb-1.5">
                        <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center text-[#F56753] font-extrabold text-xs uppercase overflow-hidden">
                          {member?.profileImageUrl ? (
                            <img src={member.profileImageUrl} alt={member?.fullName} className="w-full h-full object-cover" />
                          ) : (
                            getInitials(member?.fullName || member?.email)
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-extrabold text-slate-900 truncate max-w-[160px]">
                            {member?.fullName || 'Corporate User'}
                          </p>
                          <span className="text-[10px] font-semibold text-[#F56753] bg-orange-50 px-2 py-0.5 rounded-full uppercase tracking-wider">Admin</span>
                        </div>
                      </div>
                      <p className="text-xs font-medium text-slate-400 truncate">
                        {member?.email || ''}
                      </p>
                    </div>

                    <Link
                      to="/corporate/profile"
                      onClick={() => setProfileDropdownOpen(false)}
                      className="flex items-center gap-2 px-3.5 py-2.5 text-sm font-semibold text-slate-700 hover:text-slate-950 hover:bg-slate-50 rounded-xl transition-all text-left w-full"
                    >
                      <FaUser className="text-slate-400 text-xs" />
                      <span>Corporate Profile</span>
                    </Link>

                    <button
                      onClick={logout}
                      className="flex items-center gap-2 px-3.5 py-2.5 text-sm font-bold text-[#F56753] hover:bg-orange-50/50 rounded-xl transition-all text-left w-full mt-1 border-t border-slate-50"
                    >
                      <FaSignOutAlt className="text-red-400 text-xs" />
                      <span>Log Out Account</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Header Block */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-100 px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <span className="text-lg font-extrabold">
            <span className="text-[#F56753]">Cab</span>
            <span className="text-slate-800">Zigo</span>
            <span className="ml-1.5 text-[10px] font-bold text-slate-400 border border-slate-200 px-1.5 py-0.5 rounded-md uppercase tracking-wider bg-slate-50">Admin</span>
          </span>
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMobileMenuOpen(false)}
            style={{ display: mobileMenuOpen ? 'flex' : 'none' }}
            className="p-2 text-slate-600 hover:bg-slate-100 rounded-xl focus:outline-none items-center justify-center"
            aria-label="Close menu"
          >
            <FaTimes size={20} />
          </button>
          <button
            onClick={() => setMobileMenuOpen(true)}
            style={{ display: mobileMenuOpen ? 'none' : 'flex' }}
            className="p-2 text-slate-600 hover:bg-slate-100 rounded-xl focus:outline-none items-center justify-center"
            aria-label="Open menu"
          >
            <FaBars size={20} />
          </button>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      <div
        className={`lg:hidden fixed inset-x-0 top-16 bg-white border-b border-slate-100 p-4 z-40 shadow-lg space-y-4 max-h-[calc(100vh-4rem)] overflow-y-auto transition-all duration-300 ${
          mobileMenuOpen
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
      >
        <nav className="space-y-2">
          {navItems.map((item) => (
            item.dropdown ? (
              <div key={item.name} className="space-y-1 bg-slate-50/50 rounded-xl p-3 border border-slate-100">
                <p className="px-3 py-1.5 text-xs font-bold text-slate-400 uppercase tracking-wider">{item.name}</p>
                {item.dropdown
                  .filter((subItem) => subItem.name !== "Wallet Summary")
                  .map((subItem) => (
                    <Link
                      key={subItem.path}
                      to={subItem.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center px-3 py-2.5 text-sm font-semibold rounded-lg transition-all ${
                        isActive(subItem.path)
                          ? "bg-[#F56753] text-white shadow-sm"
                          : "text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      {subItem.name}
                    </Link>
                  ))}
              </div>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center px-3.5 py-3 text-sm font-bold rounded-xl transition-all ${
                  isActive(item.path)
                    ? "bg-slate-900 text-white shadow-sm"
                    : "text-slate-600 hover:bg-slate-50 border border-transparent hover:border-slate-100"
                }`}
              >
                {item.name}
              </Link>
            )
          ))}
        </nav>

        <div className="pt-3 border-t border-slate-100 space-y-2">
          <Link
            to="/corporate/dashboard"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center justify-center gap-2 w-full py-2.5 bg-orange-50 text-[#F56753] border border-orange-100 rounded-xl text-sm font-bold shadow-sm hover:bg-orange-100 transition-all"
          >
            <FaHome className="text-[#F56753]" />
            Go to Cabzigo Home
          </Link>

          <Link
            to="/corporate/profile"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center justify-center gap-2 w-full py-2.5 bg-slate-50 text-slate-700 border border-slate-200 rounded-xl text-sm font-bold hover:bg-slate-100 transition-all"
          >
            <FaUser className="text-slate-400" />
            Corporate Profile
          </Link>

          <button
            onClick={logout}
            className="flex items-center justify-center gap-2 w-full py-2.5 bg-orange-50 text-[#F56753] border border-orange-100 rounded-xl text-sm font-bold hover:bg-orange-100 transition-all"
          >
            <FaSignOutAlt className="text-[#F56753]" />
            Log Out Account
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-20 lg:pt-24 flex-grow w-full">
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
          {children}
        </div>
      </main>
    </div>
  );
};

export default CorporateLayout;
